import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import SettingsIcon from '@atlaskit/icon/core/settings';
import VideoPlayIcon from '@atlaskit/icon/core/video-play';
import { Box } from '@atlaskit/primitives/compiled/box';
import Inline from '@atlaskit/primitives/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { TimeAgo } from '@confluence/time-ago';

import { useFetchSpaceSuggestions } from './hooks/useFetchSpaceSuggestions.ts';
import { useRunSpaceOrganizer } from './hooks/useRunSpaceOrganizer.ts';
import { NeedsReviewSection } from './NeedsReviewSection.tsx';
import { NoSuggestionsFound } from './NoSuggestionsFound.tsx';
import { SettingsView } from './settings/SettingsView.tsx';
import type { SuggestionItem } from './SuggestionCard.tsx';

type SpaceSuggestionsProps = {
	spaceKey: string;
	/** Injected by `withFlags`; consumers only pass `spaceKey`. */
	flags: FlagsStateContainer;
};

type SpaceSuggestionsViewMode = 'list' | 'settings';

function SpaceSuggestionsComponent({ spaceKey, flags }: SpaceSuggestionsProps) {
	const { formatMessage } = useIntl();
	const { createEvent } = useConfluenceAnalyticsEvents();

	const { suggestions, isLoading, error, refetch } = useFetchSpaceSuggestions(spaceKey);
	const { run, isRunning, error: runError } = useRunSpaceOrganizer(spaceKey);

	// Suggestions the user has rejected this session, tracked by suggestion id.
	// Rejection is client-side only today; the card is hidden by filtering it out
	// of `reviewItems`, and Undo simply removes the id again. Backend persistence
	// is tracked by DISCO-5884.
	const [rejectedIds, setRejectedIds] = useState<Set<string>>(() => new Set());

	// Which body view is currently shown:
	// - 'list': the default Needs review / suggestions list (the previous behavior).
	// - 'settings': the Knowledge curator settings view, toggled via the Settings icon.
	const [view, setView] = useState<SpaceSuggestionsViewMode>('list');

	// Tracks whether the right-rail review panel is open.
	// When true, the main content area reserves space for the fixed review panel.
	// The right rail is only relevant in the 'list' view.
	const [isRightRailOpen, setIsRightRailOpen] = useState<boolean>(false);

	const openRightRail = useCallback((_item: SuggestionItem) => setIsRightRailOpen(true), []);

	const goToSettings = useCallback(() => {
		setView('settings');
		// Close the right rail when entering settings to avoid stale UI.
		setIsRightRailOpen(false);
	}, []);

	const goToList = useCallback(() => {
		setView('list');
	}, []);

	const handleLaunch = useCallback(async () => {
		try {
			await run();
			await refetch();
			// A fresh run regenerates the suggestion set, so previous session-only
			// rejections no longer apply.
			setRejectedIds(new Set());
		} catch {
			// The run and fetch hooks own their error states; this prevents an unhandled rejection.
		}
	}, [refetch, run]);

	// Approve / Reject backend wiring is tracked by DISCO-5884. Until that lands
	// the card-level Approve/Reject IconButtons render but are disabled (see
	// `isApproveRejectDisabled` below). We still fire a UI analytics event when
	// the user attempts the action — both to validate the interaction surface
	// and to gather pre-launch click signal.
	const handleApprove = useCallback(
		(_item: SuggestionItem) => {
			createEvent({
				type: 'sendUIEvent',
				data: {
					source: 'spaceSuggestionsScreen',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'spaceSuggestionApprove',
				},
			}).fire();
		},
		[createEvent],
	);

	const handleReject = useCallback(
		(item: SuggestionItem) => {
			createEvent({
				type: 'sendUIEvent',
				data: {
					source: 'spaceSuggestionsScreen',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'spaceSuggestionReject',
				},
			}).fire();

			// Remove the suggestion from the list (client-side only for now).
			setRejectedIds((prev) => new Set(prev).add(item.id));

			// Reuse the suggestion id so re-rejecting replaces the existing flag.
			const flagId = `space-suggestion-rejected-${item.id}`;
			void flags.showSuccessFlag({
				id: flagId,
				title: formatMessage(i18n.rejectedFlagTitle),
				isAutoDismiss: true,
				actions: [
					{
						content: formatMessage(i18n.rejectedFlagUndoAction),
						onClick: () => {
							createEvent({
								type: 'sendUIEvent',
								data: {
									source: 'spaceSuggestionsScreen',
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'spaceSuggestionRejectUndo',
								},
							}).fire();
							setRejectedIds((prev) => {
								const next = new Set(prev);
								next.delete(item.id);
								return next;
							});
							void flags.hideFlag(flagId);
						},
					},
					{
						content: formatMessage(i18n.rejectedFlagSendFeedbackAction),
						onClick: () => {
							// TODO(DISCO-5903 follow-up): wire to the real feedback surface.
							createEvent({
								type: 'sendUIEvent',
								data: {
									source: 'spaceSuggestionsScreen',
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'spaceSuggestionRejectSendFeedback',
								},
							}).fire();
						},
					},
				],
			});
			// TODO(DISCO-5884): persist the rejection to the backend once the endpoint exists.
		},
		[createEvent, flags, formatMessage],
	);

	const reviewItems: SuggestionItem[] = useMemo(
		() =>
			// Single pass: skip rejected suggestions while mapping to view items.
			suggestions.reduce<SuggestionItem[]>((items, suggestion) => {
				if (!rejectedIds.has(suggestion.id)) {
					items.push({
						id: suggestion.id,
						summary: suggestion.title,
						actions: suggestion.actions,
						timestamp: <TimeAgo date={suggestion.createdAt} />,
					});
				}
				return items;
			}, []),
		[suggestions, rejectedIds],
	);

	const isSettingsView = view === 'settings';
	const showRightRail = !isSettingsView && isRightRailOpen;

	return (
		<Box xcss={styles.page}>
			{/* Page header: title + action buttons (Launch + Settings/Close) */}
			<Box xcss={styles.header}>
				<Inline alignBlock="center" spread="space-between" space="space.100">
					<Heading size="large">{formatMessage(i18n.pageTitle)}</Heading>
					<Inline alignBlock="center" space="space.075">
						{!isSettingsView && (
							<Button
								iconBefore={VideoPlayIcon}
								onClick={handleLaunch}
								isLoading={isRunning}
								isDisabled={isLoading}
							>
								{formatMessage(i18n.launchButton)}
							</Button>
						)}
						{isSettingsView ? (
							<IconButton
								icon={CrossIcon}
								label={formatMessage(i18n.backButtonLabel)}
								appearance="subtle"
								isTooltipDisabled={false}
								onClick={goToList}
								testId="space-suggestions-back-button"
							/>
						) : (
							<IconButton
								icon={SettingsIcon}
								label={formatMessage(i18n.settingsLabel)}
								appearance="subtle"
								isTooltipDisabled={false}
								onClick={goToSettings}
								testId="space-suggestions-settings-button"
							/>
						)}
					</Inline>
				</Inline>
			</Box>

			{/* Responsive grid: reserves the fixed review panel width when the right rail is open */}
			<Box xcss={showRightRail ? styles.gridWithRightRail : styles.gridDefault}>
				{/* Main content area */}
				<Box xcss={styles.mainContent}>
					{isSettingsView ? (
						<SettingsView />
					) : (
						<>
							{runError && (
								<Box role="alert" testId="space-suggestions-run-error">
									<Text>{formatMessage(i18n.runError)}</Text>
									<VisuallyHidden testId="space-suggestions-run-error-cause">
										{runError.message}
									</VisuallyHidden>
								</Box>
							)}
							{isLoading && (
								<Box aria-live="polite" testId="space-suggestions-loading">
									<Text>{formatMessage(i18n.loading)}</Text>
								</Box>
							)}
							{!isLoading && error && (
								<Box role="alert" testId="space-suggestions-error">
									<Text>{formatMessage(i18n.error)}</Text>
									{/* Raw cause is exposed only to assistive tech / dev tools for diagnostics. */}
									<VisuallyHidden testId="space-suggestions-error-cause">
										{error.message}
									</VisuallyHidden>
								</Box>
							)}
							{!isLoading && !error && reviewItems.length === 0 && <NoSuggestionsFound />}
							{!isLoading && !error && reviewItems.length > 0 && (
								<NeedsReviewSection
									items={reviewItems}
									onReview={openRightRail}
									onApprove={handleApprove}
									onReject={handleReject}
								/>
							)}
						</>
					)}
				</Box>

				{/* Right rail — placeholder slot; content to be implemented in a separate ticket */}
				{showRightRail && (
					<Box xcss={styles.rightRail} testId="right-rail">
						{/* TODO: Render right-rail review panel here */}
					</Box>
				)}
			</Box>
		</Box>
	);
}

export const SpaceSuggestions = withFlags(SpaceSuggestionsComponent);

const i18n = defineMessages({
	pageTitle: {
		id: 'space-suggestions.page.knowledge-curator-title',
		defaultMessage: 'Knowledge curator',
		description: 'The main heading for the Knowledge curator (Space Suggestions) page',
	},
	launchButton: {
		id: 'space-suggestions.page.launch-button',
		defaultMessage: 'Launch',
		description:
			'Label for the Launch button in the Knowledge curator page header. Runs Space Organizer for this space.',
	},
	settingsLabel: {
		id: 'space-suggestions.page.settings-button-label',
		defaultMessage: 'Go to settings',
		description:
			'Accessible label and tooltip for the settings icon button in the Knowledge curator page header',
	},
	backButtonLabel: {
		id: 'space-suggestions.page.back-button-label',
		defaultMessage: 'Back to suggestions',
		description:
			'Accessible label and tooltip for the close (X) icon button shown in the Knowledge curator page header while the settings view is open. Clicking it returns the user to the Needs review / suggestions list.',
	},
	loading: {
		id: 'space-suggestions.page.loading',
		defaultMessage: 'Loading…',
		description: 'Shown while the Space Suggestions page is fetching data',
	},
	error: {
		id: 'space-suggestions.page.error',
		defaultMessage: 'Something went wrong while loading suggestions. Please try again later.',
		description:
			'Shown when the Space Suggestions page fails to fetch data from the backend. Generic message — the raw error text is only surfaced via the element title attribute for diagnostics.',
	},
	rejectedFlagTitle: {
		id: 'space-suggestions.page.rejected-flag-title',
		defaultMessage: 'Suggestion rejected',
		description:
			'Title of the confirmation flag shown after the user rejects (dismisses) a suggestion from the Knowledge curator list',
	},
	rejectedFlagUndoAction: {
		id: 'space-suggestions.page.rejected-flag-undo-action',
		defaultMessage: 'Undo',
		description:
			'Label for the action on the "Suggestion rejected" flag that restores the rejected suggestion to the list',
	},
	rejectedFlagSendFeedbackAction: {
		id: 'space-suggestions.page.rejected-flag-send-feedback-action',
		defaultMessage: 'Send feedback',
		description:
			'Label for the action on the "Suggestion rejected" flag that lets the user send feedback about the rejected suggestion',
	},
	runError: {
		id: 'space-suggestions.page.run-error',
		defaultMessage: 'Something went wrong while running Space Organizer. Please try again later.',
		description:
			'Shown when the user tries to manually run Space Organizer and the run request fails. Generic message — the raw error text is only surfaced to assistive technology for diagnostics.',
	},
});

const styles = cssMap({
	page: {
		paddingBlockStart: token('space.300'),
		paddingBlockEnd: token('space.300'),
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
	},
	header: {
		marginBottom: token('space.300'),
	},
	mainContent: {
		minWidth: '0',
	},
	rightRail: {
		minWidth: '0',
	},
	/**
	 * Grid layout variants:
	 * - gridDefault: main content takes full width
	 * - gridWithRightRail: main content fills the remaining space, right rail matches the fixed panel width
	 */
	gridDefault: {
		display: 'grid',
		gridTemplateColumns: '1fr',
	},
	gridWithRightRail: {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr) 400px',
		columnGap: token('space.300'),
	},
});
